import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import LogRocket from 'logrocket';

import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../../store/global-selectors';
import { Pill } from 'ui-components/components/pill';
import { noop } from 'ui-components/default-props/noop';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenuType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Button } from 'ui-components/components/button';
import { updateProductWarehouseAssociations } from '../../../../store/products/products.actions';

import tagDropdownStyle from '../../../../components/tag-dropdown/tag-dropdown.module.less';
import style from '../../../../components/grid-header/shared-classes.module.less';
import messages from '../../messages';
import { useProductContext } from '../product-context';
import actionButtonStyle from './action-button-style.module.less';

export const ActiveShippingLocationsButton = () => {
  const { selectedProductIds } = useProductContext();

  const [locallySelectedWarehouses, setLocallySelectedWarehouses] = useState([]);
  const [dbConfiguredSelectedWarehouses, setDbConfiguredSelectedWarehouses] = useState([]);

  const warehousesDeselectedToSave = useMemo(
    () =>
      dbConfiguredSelectedWarehouses.filter(
        (dbWarehouse) => !locallySelectedWarehouses.some((localWarehouse) => localWarehouse === dbWarehouse),
      ),
    [locallySelectedWarehouses, dbConfiguredSelectedWarehouses],
  );

  const dispatch = useDispatch();

  const resetLocallySelectedWarehouses = useCallback(() => {
    setLocallySelectedWarehouses(dbConfiguredSelectedWarehouses);
  }, [dbConfiguredSelectedWarehouses, setLocallySelectedWarehouses]);

  const addRemoveProductWarehouseAssociations = useCallback(() => {
    const environment = process.env.NODE_ENV || 'development'; // "development" or "production"
    LogRocket.track('INTERACT_WITH_ACTIVE_SHIPPING_LOCATIONS_ACTION_BUTTON', {
      env: environment,
    });
    const warehouseIdsToAdd = locallySelectedWarehouses.map((warehouse) => warehouse.shipFromId);
    const warehouseIdsToDelete = warehousesDeselectedToSave.map((warehouse) => warehouse.shipFromId);
    dispatch(updateProductWarehouseAssociations(selectedProductIds, warehouseIdsToAdd, warehouseIdsToDelete));
  }, [locallySelectedWarehouses, warehousesDeselectedToSave, dispatch, selectedProductIds]);

  const warehouses = useSelector(Global.selectSellerShipFroms) || emptyArray;
  const selectedProducts =
    useSelector((state) =>
      Global.selectEntitiesProductsByIds(state, {
        productIds: selectedProductIds,
      }),
    ) || emptyArray;
  const shouldBeDisabled = !selectedProductIds.length;

  useEffect(() => {
    const selectedWarehouseIdsSet = selectedProducts.reduce((acc, product) => {
      const productWarehouses = product?.productWarehouses || emptyArray;
      productWarehouses.filter((warehouse) => warehouse.isSelected).forEach((w) => acc.add(w.warehouseId.toString()));

      return acc;
    }, new Set());

    const warehousesForSelectedProducts = warehouses.filter((w) => selectedWarehouseIdsSet.has(w.shipFromId.toString()));

    setLocallySelectedWarehouses(warehousesForSelectedProducts);
    setDbConfiguredSelectedWarehouses(warehousesForSelectedProducts);
  }, [selectedProducts, warehouses]);

  const onWarehouseToggled = useCallback(
    (warehouse) => {
      const selectedWarehousesForDisplay = new Set(locallySelectedWarehouses);

      if (locallySelectedWarehouses.indexOf(warehouse) !== -1) {
        selectedWarehousesForDisplay.delete(warehouse);
      } else {
        selectedWarehousesForDisplay.add(warehouse);
      }
      setLocallySelectedWarehouses(Array.from(selectedWarehousesForDisplay));
    },
    [locallySelectedWarehouses, setLocallySelectedWarehouses],
  );

  const shouldAllowDropdownToClose = useCallback((e) => {
    if (!e.target.classList || !e.target.classList.length) {
      return false;
    }
    return [...new Set(e.target.classList)].some((cssClass) => {
      return cssClass.indexOf('button-active-shipping-locations') !== -1;
    });
  }, []);

  return (
    <DropdownMenu
      closeWhenChildrenClicked={false}
      containerAllowedMeToClose={shouldAllowDropdownToClose}
      dropdownMenuClassName={style['dropdown-menu']}
      dropdownMenuType={DropdownMenuType.ComboBox}
      menuPosition='bottom-right'
    >
      <DropdownMenuToggler className={style['action-button']} onClick={resetLocallySelectedWarehouses} theme='tertiary'>
        <FormattedMessage {...messages.activeShippingLocationsButton} />
      </DropdownMenuToggler>
      {warehouses.map((warehouse) => (
        <DropdownMenuItem
          key={warehouse.shipFromId}
          className={style['dropdown-menu-item']}
          disabled={shouldBeDisabled}
          onClick={() => onWarehouseToggled(warehouse)}
        >
          <Checkbox
            checked={locallySelectedWarehouses.indexOf(warehouse) !== -1}
            className={tagDropdownStyle.checkbox}
            disabled={shouldBeDisabled}
            onChange={noop}
          />
          <Pill className={tagDropdownStyle.pill} label={warehouse.name} theme='no-status' truncate />
        </DropdownMenuItem>
      ))}
      <div className={actionButtonStyle['action-button-footer']}>
        <Button className='button-active-shipping-locations' onClick={resetLocallySelectedWarehouses} theme='link'>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <Button
          className='button-active-shipping-locations'
          disabled={shouldBeDisabled}
          onClick={addRemoveProductWarehouseAssociations}
          theme='primary'
        >
          <FormattedMessage {...messages.save} />
        </Button>
      </div>
    </DropdownMenu>
  );
};
